import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _setTimeout from "@babel/runtime-corejs3/core-js/set-timeout";
import React from "react";
import PropTypes from "prop-types";
import noop from "lodash/noop";
import { RETURN, ESC, SPACE, UP, DOWN } from "@xo-union/constant-key-codes";
import { useSelectContext } from "../SelectContext/index.js";
import { useElementIDAndRefSync, useElementRef } from "../ElementSync/index.js";
const useTextBuffer = () => {
  const [textBuffer, setTextBuffer] = React.useState('');
  const timeoutRef = React.useRef();
  const addChar = char => {
    clearTimeout(timeoutRef.current);
    setTextBuffer(text => [text, char].join(''));
    timeoutRef.current = _setTimeout(() => {
      setTextBuffer('');
    }, 200);
  };
  return [textBuffer, addChar];
};
const useEventHandlerProps = () => {
  const select = useSelectContext();
  const buttonRef = useElementRef('MenuButton');
  const [textBuffer, addChar] = useTextBuffer();
  const onKeyDown = e => {
    if (e.keyCode === RETURN || e.keyCode === SPACE) {
      select.activeOption.select(e);
      _setTimeout(() => {
        buttonRef.current.focus();
      });
      e.preventDefault();
      return;
    }
    if (e.keyCode === DOWN) {
      select.rotateActiveIndex(1);
      e.preventDefault();
      return;
    }
    if (e.keyCode === UP) {
      select.rotateActiveIndex(-1);
      e.preventDefault();
      return;
    }
    if (e.keyCode === ESC) {
      buttonRef.current.focus();

      // Prevent modals from being closed if rendered in modal
      e.nativeEvent.stopImmediatePropagation();
      return;
    }
    if (e.key.length === 1) {
      addChar(e.key);
    }
  };
  React.useEffect(() => {
    if (textBuffer.length > 0) select.activateOptionMatchingText(textBuffer);
  }, [textBuffer]);
  return {
    onKeyDown: select.disabled ? noop : onKeyDown,
    onBlur: select.close
  };
};
const useListBoxFocusOnSelectOpen = () => {
  const select = useSelectContext();
  const ref = useElementRef('ListBox');
  React.useEffect(() => {
    const timeout = _setTimeout(() => {
      if (select.isOpen) {
        ref.current.focus();
      }
    });
    return () => {
      clearTimeout(timeout);
    };
  }, [select.isOpen, ref]);
};

/**
 * Renders the container for all custom options.
 *
 * Focus is added to this element when the Select is open.
 *
 * Additional props are passed to the underlying element.
 */
const ListBox = _ref => {
  var _select$activeOption;
  let {
    as: ComponentToRender,
    id: idProp,
    ...props
  } = _ref;
  const select = useSelectContext();
  const eventHandlerProps = useEventHandlerProps();
  const activeDescendant = (_select$activeOption = select.activeOption) === null || _select$activeOption === void 0 ? void 0 : _select$activeOption.id;
  const [id, ref] = useElementIDAndRefSync('ListBox', idProp);
  useListBoxFocusOnSelectOpen();
  return /*#__PURE__*/React.createElement(ComponentToRender, _extends({
    ref: ref,
    role: "listbox",
    id: id,
    "aria-activedescendant": activeDescendant,
    tabIndex: select.isOpen ? '0' : '-1'
  }, eventHandlerProps, props));
};
process.env.NODE_ENV !== "production" ? ListBox.propTypes = {
  /**
   * Underlying component to render
   */
  as: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /**
   * Override listbox id
   */
  id: PropTypes.string
} : void 0;
ListBox.defaultProps = {
  as: 'ul'
};
export default ListBox;